import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withHashLocation, withInMemoryScrolling } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorHandler } from '@angular/core';
import { authInterceptor } from './app/auth.interceptor';
import { errorInterceptor } from './app/error.interceptor';
import { retryInterceptor } from './app/retry.interceptor';
import { registerOfflineServiceWorker } from './sw-register';
import { authGuard } from './app/auth.guard';
import { anonymousGuard, rootRedirectTo } from './app/anonymous.guard';
import { GlobalErrorHandler } from './app/global-error-handler';
import { apolloProvider } from './app/graphql/apollo.config';
import { AppComponent } from './app/app.component';

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(
      [
        // Root-route branching: authed visitors land on the orders/payments
        // dashboard, anonymous visitors land on the public marketing page.
        { path: '', pathMatch: 'full', redirectTo: rootRedirectTo },
        {
          path: 'home',
          loadComponent: () => import('./app/home/home.component').then((m) => m.HomeComponent),
        },
        {
          path: 'about',
          loadComponent: () => import('./app/about/about.component').then((m) => m.AboutComponent),
        },
        {
          path: 'account',
          loadComponent: () =>
            import('./app/account/account.component').then((m) => m.AccountComponent),
          canActivate: [authGuard],
        },
        {
          // Authenticated landing surface: orders, payments, and other
          // post-purchase tabs. Reuses AccountComponent for now; a dedicated
          // DashboardComponent will replace this in a follow-up PR.
          path: 'dashboard',
          loadComponent: () =>
            import('./app/account/account.component').then((m) => m.AccountComponent),
          canActivate: [authGuard],
          data: { defaultTab: 'purchases' },
        },
        {
          path: 'login',
          loadComponent: () => import('./app/login/login.component').then((m) => m.LoginComponent),
          canActivate: [anonymousGuard],
        },
        {
          // Authenticated landing surface: orders, payments, and other
          // post-purchase tabs. Reuses AccountComponent for now; a dedicated
          // DashboardComponent will replace this in a follow-up PR.
          path: 'dashboard',
          loadComponent: () =>
            import('./app/account/account.component').then((m) => m.AccountComponent),
          canActivate: [authGuard],
          data: { defaultTab: 'purchases' },
        },
        {
          path: 'signup',
          loadComponent: () =>
            import('./app/signup/signup.component').then((m) => m.SignupComponent),
          canActivate: [anonymousGuard],
        },
        {
          // UppCoin mobile-app embed (React Native / Expo Web bundle
          // served from CloudFront, mounted via UppcoinWidgetComponent).
          // Auth-gated: the widget reuses the host's session JWT.
          path: 'uppcoin',
          loadComponent: () =>
            import('./app/components/uppcoin-widget/uppcoin-widget.component').then(
              (m) => m.UppcoinWidgetComponent,
            ),
          canActivate: [authGuard],
        },
        {
          // Verify-by-link landing route. Public on purpose: an anonymous
          // user clicking the email link must be able to confirm their
          // address before they have a session.
          path: 'verify-email',
          loadComponent: () =>
            import('./app/verify-email/verify-email.component').then((m) => m.VerifyEmailComponent),
        },
        {
          // Forgot-password request page. Public — the user is, by
          // definition, locked out of their account when they need it.
          path: 'forgot-password',
          loadComponent: () =>
            import('./app/forgot-password/forgot-password.component').then(
              (m) => m.ForgotPasswordComponent,
            ),
        },
        {
          // Reset-password landing route — the destination of the link
          // delivered by the password-reset email. Public for the same
          // reason as /forgot-password.
          path: 'reset-password',
          loadComponent: () =>
            import('./app/reset-password/reset-password.component').then(
              (m) => m.ResetPasswordComponent,
            ),
        },
        {
          path: 'privacy',
          loadComponent: () =>
            import('./app/privacy/privacy.component').then((m) => m.PrivacyComponent),
        },
        {
          path: 'cookie-policy',
          loadComponent: () =>
            import('./app/cookie-policy/cookie-policy.component').then(
              (m) => m.CookiePolicyComponent,
            ),
        },
        {
          path: 'terms',
          loadComponent: () => import('./app/terms/terms.component').then((m) => m.TermsComponent),
        },
        {
          path: 'security',
          loadComponent: () =>
            import('./app/security/security.component').then((m) => m.SecurityComponent),
        },
        {
          path: 'checkout',
          loadComponent: () =>
            import('./app/checkout/checkout.component').then((m) => m.CheckoutComponent),
          canActivate: [authGuard],
        },
        {
          path: 'contact',
          loadComponent: () =>
            import('./app/contact/contact.component').then((m) => m.ContactComponent),
        },
        {
          path: 'services',
          loadComponent: () =>
            import('./app/services-page/services-page.component').then(
              (m) => m.ServicesPageComponent,
            ),
        },
        {
          path: 'calendar',
          loadComponent: () =>
            import('./app/calendar/calendar.component').then((m) => m.CalendarComponent),
        },
        {
          path: '**',
          loadComponent: () =>
            import('./app/not-found/not-found.component').then((m) => m.NotFoundComponent),
        },
      ],
      withHashLocation(),
      // Scroll to the top of the page on forward navigations with no
      // fragment (e.g. clicking a header menu item) and restore position on
      // back/forward. anchorScrolling is enabled so navigations that DO carry
      // a fragment (e.g. /services#row-naics) scroll to that section instead
      // of the top; feature pages also re-scroll after their @for content
      // renders to correct for layout shift.
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
    ),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor, retryInterceptor, errorInterceptor])),
    apolloProvider,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
})
  .then(() => registerOfflineServiceWorker())
  .catch((err) => console.error(err));
